import React, { useState, useRef, useEffect } from "react";
import { apiClient } from "@/services/apiClient";
import { useAppStore } from "@/core/store/useAppStore";
import { useQueryClient } from "@tanstack/react-query";

interface BulkRotateDropdownProps {
  selectedIds: number[];
  onComplete: () => void;
  disabled?: boolean;
}

const DIRECTIONS = [
  { value: "clockwise" as const, label: "Rotate Clockwise", icon: "cw" },
  { value: "counterclockwise" as const, label: "Rotate Counter-clockwise", icon: "ccw" },
];

export const BulkRotateDropdown: React.FC<BulkRotateDropdownProps> = ({
  selectedIds,
  onComplete,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedClient = useAppStore((state) => state.selectedClient);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleRotate = async (direction: "clockwise" | "counterclockwise") => {
    if (!selectedClient?.id || selectedIds.length === 0) return;

    setIsLoading(true);
    try {
      await apiClient.bulkRotateVideos(selectedClient.id, selectedIds, direction);
      queryClient.invalidateQueries({ queryKey: ["media"] });
      setIsOpen(false);
      onComplete();
    } catch (error) {
      console.error("Failed to bulk rotate videos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Rotating...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Rotate
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {DIRECTIONS.map((dir) => (
            <button
              key={dir.value}
              onClick={() => handleRotate(dir.value)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 transition-colors"
            >
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                style={dir.icon === "ccw" ? { transform: "scaleX(-1)" } : undefined}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9" />
              </svg>
              {dir.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
